import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../hooks/useAuth'
import {
  Search,
  Plus,
  BookOpen,
  Clock,
  Target,
  Filter,
  Grid3X3,
  List,
  Star,
  Users,
  Lock,
  Play,
  BarChart3,
  Settings,
  ChevronDown,
  Eye,
  Heart,
  TrendingUp,
  RotateCcw
} from 'lucide-react'
import { RootState } from '../store'
import {
  fetchDecks,
  setCurrentDeck,
  searchFlashcards,
  setFilter
} from '../store/slices/flashcardSlice'
import { FlashcardDeck, FlashcardFilter } from '../types/flashcard'
import { LGS_SUBJECTS, getSubjectByValue, DIFFICULTY_LEVELS } from '../constants/lgsSubjects'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
// LoadingSpinner removed - no loading state needed
import ImportFlashcards from '../components/ImportFlashcards'

const Flashcards: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const {
    decks,
    isLoading: loading,
    error,
    filter,
    searchResults
  } = useSelector((state: RootState) => state.flashcard)
  const { loading: authLoading } = useSelector((state: RootState) => state.auth)
  const isRehydrated = useSelector((state: RootState) => state._persist?.rehydrated)

  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showFilters, setShowFilters] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [localFilter, setLocalFilter] = useState<FlashcardFilter>({
    subject: '',
    topic: '',
    difficulty: undefined,
    isPublic: undefined
  })

  // LGS subjects from constants
  const subjects = LGS_SUBJECTS

  // Fetch decks when user is authenticated and after rehydration
  useEffect(() => {
    if (isAuthenticated && isRehydrated) {
      dispatch(fetchDecks() as any)
    }
  }, [isAuthenticated, isRehydrated, dispatch])

  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        dispatch(searchFlashcards({ query: searchQuery, filter: localFilter }) as any)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchQuery, localFilter, dispatch])

  const handleFilterChange = (newFilter: Partial<FlashcardFilter>) => {
    const updatedFilter = { ...localFilter, ...newFilter }
    setLocalFilter(updatedFilter)
    dispatch(setFilter(updatedFilter))
  }

  const handleDeckSelect = (deck: FlashcardDeck) => {
    dispatch(setCurrentDeck(deck))
  }

  const getSubjectColor = (subject: string) => {
    const subjectData = getSubjectByValue(subject)
    return subjectData?.color || 'bg-gray-500'
  }

  const getSubjectLabel = (subject: string) => {
    const subjectData = getSubjectByValue(subject)
    return subjectData?.label || subject
  }

  const getSubjectIcon = (subject: string) => {
    const subjectData = getSubjectByValue(subject)
    return subjectData?.icon || '📚'
  }

  const getDifficultyColor = (difficulty: string) => {
    const difficultyData = DIFFICULTY_LEVELS.find(d => d.value === difficulty)
    return difficultyData?.color || 'bg-gray-100 text-gray-800'
  }

  const getDifficultyLabel = (difficulty: string) => {
    const difficultyData = DIFFICULTY_LEVELS.find(d => d.value === difficulty)
    return difficultyData?.label || difficulty
  }

  const displayDecks = searchQuery.trim() ? searchResults?.decks || [] : decks

  // Show loading spinner during initial deck fetch
  if (authLoading || (isAuthenticated && loading && decks.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Desteler yükleniyor...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Giriş Yapmanız Gerekiyor</h2>
          <p className="text-gray-600 mb-6">Flashcard'larınızı görüntülemek için lütfen giriş yapın.</p>
          <Link to="/login">
            <Button className="bg-blue-600 hover:bg-blue-700">
              Giriş Yap
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-indigo-900/20">
      {/* Modern Header */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-8 text-white mb-8">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex flex-col gap-4 lg:gap-6">
                  <div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                      Flashcard Destelerim
                    </h1>
                    <p className="text-blue-100 text-sm sm:text-base lg:text-lg">
                      LGS konularını flashcard'larla çalış ve başarıya ulaş
                    </p>
                  </div>
                  
                  {/* Modern Search and Controls */}
                  <div className="flex flex-col gap-4">
                    <div className="relative w-full">
                      <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-blue-200 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        placeholder="Deste veya kart ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowFilters(!showFilters)}
                          className={`group relative overflow-hidden px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                            showFilters
                              ? 'bg-white/30 text-white shadow-lg'
                              : 'bg-white/20 text-blue-100 hover:bg-white/30'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4" />
                            <span className="hidden sm:inline">Filtrele</span>
                            <span className="sm:hidden">Filtre</span>
                            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                              showFilters ? 'rotate-180' : ''
                            }`} />
                          </div>
                        </button>
                        
                        <div className="flex bg-white/20 rounded-2xl p-1">
                          <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-xl transition-all duration-300 ${
                              viewMode === 'grid'
                                ? 'bg-white/30 text-white shadow-lg'
                                : 'text-blue-200 hover:bg-white/20'
                            }`}
                          >
                            <Grid3X3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-xl transition-all duration-300 ${
                              viewMode === 'list'
                                ? 'bg-white/30 text-white shadow-lg'
                                : 'text-blue-200 hover:bg-white/20'
                            }`}
                          >
                            <List className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowReviewModal(true)}
                          className="group relative overflow-hidden px-3 sm:px-6 py-2.5 sm:py-3 bg-white/20 text-blue-100 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 hover:bg-white/30"
                        >
                          <div className="flex items-center gap-2">
                            <RotateCcw className="w-4 h-4" />
                            <span className="hidden sm:inline">Tekrar Et</span>
                            <span className="sm:hidden">Tekrar</span>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => setShowImportModal(true)}
                          className="group relative overflow-hidden px-3 sm:px-6 py-2.5 sm:py-3 bg-white/20 text-blue-100 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 hover:bg-white/30"
                        >
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            <span className="hidden sm:inline">İçe Aktar</span>
                            <span className="sm:hidden">İçe Al</span>
                          </div>
                        </button>
                        
                        <Link to="/flashcards/create">
                          <button className="group relative overflow-hidden px-3 sm:px-6 py-2.5 sm:py-3 bg-white text-blue-600 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 hover:bg-blue-50">
                            <div className="flex items-center gap-2">
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Yeni Deste</span>
                              <span className="sm:hidden">Yeni</span>
                            </div>
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
          </div>
        </div>



        {/* Modern Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-4 md:p-6 shadow-xl border border-white/20 max-w-7xl mx-auto mb-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {/* Subject Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Ders
                  </label>
                  <select
                    value={localFilter.subject || ''}
                    onChange={(e) => handleFilterChange({ subject: e.target.value || undefined })}
                    className="w-full p-2 md:p-3 text-sm md:text-base border border-gray-200 dark:border-gray-600 rounded-2xl bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Tüm Dersler</option>
                    {subjects.map(subject => (
                      <option key={subject.value} value={subject.value}>
                        {subject.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Difficulty Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Zorluk
                  </label>
                  <select
                    value={localFilter.difficulty || ''}
                    onChange={(e) => handleFilterChange({ difficulty: e.target.value as any || undefined })}
                    className="w-full p-2 md:p-3 text-sm md:text-base border border-gray-200 dark:border-gray-600 rounded-2xl bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Tüm Seviyeler</option>
                    {DIFFICULTY_LEVELS.map(difficulty => (
                      <option key={difficulty.value} value={difficulty.value}>
                        {difficulty.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Tür
                  </label>
                  <select
                    value={localFilter.isPublic === undefined ? '' : localFilter.isPublic ? 'public' : 'private'}
                    onChange={(e) => {
                      const value = e.target.value
                      handleFilterChange({ 
                        isPublic: value === '' ? undefined : value === 'public' 
                      })
                    }}
                    className="w-full p-2 md:p-3 text-sm md:text-base border border-gray-200 dark:border-gray-600 rounded-2xl bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Tümü</option>
                    <option value="private">Kendi Destelerim</option>
                    <option value="public">Paylaşılan Desteler</option>
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setLocalFilter({
                        subject: '',
                        topic: '',
                        difficulty: undefined,
                        isPublic: undefined
                      })
                      dispatch(setFilter({}))
                    }}
                    className="w-full p-2 md:p-3 text-sm md:text-base bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-2xl font-semibold hover:from-red-600 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
                  >
                    Filtreleri Temizle
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6"
        >
          <div className="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm border border-red-200 dark:border-red-800 rounded-3xl p-6">
            <p className="text-red-800 dark:text-red-200 font-medium">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Modern Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 dark:border-gray-700/50 hover:scale-[1.02]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10 flex flex-col sm:flex-row items-center text-center sm:text-left">
              <div className="p-2 md:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl md:rounded-2xl mb-2 sm:mb-0 sm:mr-3 md:mr-4 shadow-lg">
                <BookOpen className="text-white w-4 h-4 md:w-6 md:h-6" />
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Toplam Deste</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">{decks.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 dark:border-gray-700/50 hover:scale-[1.02]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10 flex flex-col sm:flex-row items-center text-center sm:text-left">
              <div className="p-2 md:p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl md:rounded-2xl mb-2 sm:mb-0 sm:mr-3 md:mr-4 shadow-lg">
                <Target className="text-white w-4 h-4 md:w-6 md:h-6" />
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Toplam Kart</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  {decks.reduce((total, deck) => total + deck.totalCards, 0)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 dark:border-gray-700/50 hover:scale-[1.02]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10 flex flex-col sm:flex-row items-center text-center sm:text-left">
              <div className="p-2 md:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl md:rounded-2xl mb-2 sm:mb-0 sm:mr-3 md:mr-4 shadow-lg">
                <Clock className="text-white w-4 h-4 md:w-6 md:h-6" />
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Bugün Çalışılan</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">0</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/20 dark:border-gray-700/50 hover:scale-[1.02]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10 flex flex-col sm:flex-row items-center text-center sm:text-left">
              <div className="p-2 md:p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl md:rounded-2xl mb-2 sm:mb-0 sm:mr-3 md:mr-4 shadow-lg">
                <TrendingUp className="text-white w-4 h-4 md:w-6 md:h-6" />
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Başarı Oranı</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">0%</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Modern Decks Grid/List */}
        {displayDecks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-gray-800/80 dark:to-gray-900/80 backdrop-blur-sm rounded-3xl p-6 md:p-12 max-w-md mx-auto">
              <BookOpen className="w-16 h-16 text-blue-400 mx-auto mb-6" />
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz deste oluşturmamışsın'}
              </h3>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6">
                {searchQuery 
                  ? 'Farklı anahtar kelimeler deneyin veya filtreleri değiştirin'
                  : 'İlk flashcard desteni oluşturarak çalışmaya başla'
                }
              </p>
              {!searchQuery && (
                <Link to="/flashcards/create">
                  <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 md:px-6 py-2 md:py-3 text-sm md:text-base rounded-2xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">İlk Desteni Oluştur</span>
                      <span className="sm:hidden">Oluştur</span>
                    </div>
                  </button>
                </Link>
              )}
            </div>
          </motion.div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6'
            : 'space-y-3 md:space-y-4'
          }>
            {displayDecks.map((deck, index) => (
              <motion.div
                key={deck.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`group cursor-pointer ${
                  viewMode === 'list' ? '' : ''
                }`}
                onClick={() => handleDeckSelect(deck)}
              >
                {viewMode === 'grid' ? (
                  // Modern Grid View
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700 overflow-hidden relative h-full flex flex-col min-h-[320px]">
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-900/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Header */}
                    <div className="relative flex items-start justify-between mb-4 md:mb-6">
                      <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br ${getSubjectColor(deck.subject)} flex items-center justify-center text-white text-lg md:text-xl font-bold shadow-lg`}>
                        {getSubjectIcon(deck.subject)}
                      </div>
                      <div className="flex items-center gap-3">
                        {deck.isPublic ? (
                          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                            <Users className="text-green-600 dark:text-green-400" size={16} />
                          </div>
                        ) : (
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-xl">
                            <Lock className="text-gray-600 dark:text-gray-400" size={16} />
                          </div>
                        )}
                        {deck.isFavorite && (
                          <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                            <Star className="text-yellow-500 fill-current" size={16} />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="relative text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                      {deck.title}
                    </h3>
                    
                    {/* Description */}
                    {deck.description && (
                      <p className="relative text-gray-600 dark:text-gray-400 mb-3 md:mb-4 line-clamp-2 text-xs md:text-sm leading-relaxed">
                        {deck.description}
                      </p>
                    )}
                    
                    {/* Tags */}
                    <div className="relative flex flex-wrap gap-1 md:gap-2 mb-4 md:mb-6">
                      <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-bold text-gray-900 dark:text-white ${getSubjectColor(deck.subject)} shadow-md truncate max-w-[120px]`} title={getSubjectLabel(deck.subject)}>
                        {getSubjectLabel(deck.subject)}
                      </span>
                      {deck.topic && (
                        <span className="px-2 md:px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                          {deck.topic}
                        </span>
                      )}
                      <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${getDifficultyColor(deck.difficulty)}`}>
                        {getDifficultyLabel(deck.difficulty)}
                      </span>
                    </div>
                    
                    {/* Stats */}
                    <div className="relative flex items-center justify-between text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-4 md:mb-6 mt-auto">
                      <div className="flex items-center gap-1">
                        <Target size={16} className="text-blue-500" />
                        <span className="font-medium">{deck.totalCards} kart</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={16} className="text-purple-500" />
                        <span>{new Date(deck.updatedAt).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="relative flex gap-2 md:gap-3 mt-auto">
                      <Link 
                        to={`/flashcards/study/${deck.id}`}
                        className="flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 md:py-3 px-3 md:px-4 rounded-2xl text-sm md:text-base font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2">
                          <Play size={16} />
                          <span>Çalış</span>
                        </button>
                      </Link>
                      <Link 
                        to={`/flashcards/deck/${deck.id}/settings`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button className="p-2 md:p-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-2xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 transform hover:scale-105">
                          <Settings size={16} />
                        </button>
                      </Link>
                    </div>
                   </div>
                ) : (
                  // Modern List View
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 relative overflow-hidden">
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 to-purple-50/30 dark:from-gray-800/30 dark:to-gray-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center gap-3 md:gap-6">
                        <div className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${getSubjectColor(deck.subject)} flex items-center justify-center text-white text-lg md:text-xl font-bold shadow-lg`}>
                          {getSubjectIcon(deck.subject)}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                            {deck.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                            <span className={`px-2 md:px-3 py-1 rounded-lg text-xs font-bold text-gray-900 dark:text-white ${getSubjectColor(deck.subject)} shadow-md`}>
                              {getSubjectLabel(deck.subject)}
                            </span>
                            {deck.topic && (
                              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs">
                                {deck.topic}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Target size={14} className="text-blue-500" />
                              <span className="font-medium">{deck.totalCards} kart</span>
                            </span>
                            <span className={`px-2 py-1 rounded-lg text-white font-medium bg-gradient-to-r ${getDifficultyColor(deck.difficulty)} text-xs`}>
                              {getDifficultyLabel(deck.difficulty)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 md:gap-4">
                        <div className="flex items-center gap-2">
                          {deck.isPublic ? (
                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                              <Users className="text-green-600 dark:text-green-400" size={16} />
                            </div>
                          ) : (
                            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-xl">
                              <Lock className="text-gray-600 dark:text-gray-400" size={16} />
                            </div>
                          )}
                          {deck.isFavorite && (
                            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                              <Star className="text-yellow-500 fill-current" size={16} />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 md:gap-3">
                          <Link 
                            to={`/flashcards/study/${deck.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-3 md:px-4 rounded-xl text-sm md:text-base font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                              <Play size={16} />
                              <span>Çalış</span>
                            </button>
                          </Link>
                          <Link 
                            to={`/flashcards/deck/${deck.id}/settings`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 transform hover:scale-105">
                              <Settings size={16} />
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
               </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Modern Review Modal */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl p-4 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                  Tekrar Edilecek Kartları Seç
                </h2>
                <button
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 text-lg"
                  onClick={() => setShowReviewModal(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Hangi dersten tekrar kartlarını çalışmak istiyorsunuz?
                </p>
                
                <div className="grid grid-cols-1 gap-3">
                  {subjects.map(subject => {
                    const subjectDecks = decks.filter(deck => deck.subject === subject.value)
                    if (subjectDecks.length === 0) return null
                    
                    return (
                      <button
                        key={subject.value}
                        onClick={() => {
                          setShowReviewModal(false)
                          navigate(`/flashcards/review/${subject.value}`)
                        }}
                        className={`group relative overflow-hidden p-4 rounded-2xl text-left transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg bg-gradient-to-r ${subject.color} text-white`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">{subject.icon}</div>
                            <div>
                              <h3 className="font-semibold text-lg">{subject.label}</h3>
                              <p className="text-white/80 text-sm">{subjectDecks.length} deste mevcut</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <RotateCcw className="w-5 h-5" />
                            <span className="text-sm font-medium">Tekrar Et</span>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
                
                {decks.length === 0 && (
                  <div className="text-center py-8">
                    <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Henüz deste bulunmuyor
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Tekrar etmek için önce flashcard destesi oluşturmalısınız.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modern Import Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl p-4 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                  Flashcard Destelerini İçe Aktar
                </h2>
                <button
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 text-lg"
                  onClick={() => setShowImportModal(false)}
                >
                  ✕
                </button>
              </div>
              <ImportFlashcards onComplete={() => {
                setShowImportModal(false)
                dispatch(fetchDecks() as any)
              }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Flashcards